import { useEffect, useMemo } from 'react';
import dayjs from 'dayjs';

import { DEFAULT_DATE_FORMAT, EMPTY_FIELD_VALUE, RAW_DATE_FORMAT } from '@/common/constants';
import { API_URL } from '@/dtos';
import { ReportSummaryResponse } from '@/modules/Reports/report.types';

import { useGetEmployerIdParam } from '../../hooks';
import { useBaseReportSummaryQuery } from '../../queries';
import { CardStatusSummaryReportParams } from '../CardStatusReport.types';

export const QUERY_KEY = 'GET_REPORT_CARD_SUMMARY_SUMMARY';
interface ClaimResponse extends ReportSummaryResponse {
  date_from: string;
  date_to: string;
}
const useCardStatusReportSummaryQuery = (params: CardStatusSummaryReportParams) => {
  const getEmployerIdParam = useGetEmployerIdParam({
    isMultipleEmployerMode: params.isMultipleEmployer || false,
  });
  const employerId = getEmployerIdParam(params.employerId, params.employerIds);

  const {
    data,
    rawData,
    isLoading,
    isLoadingMore,
    isSuccess,
    isEmptyData,
    lastTimeUpdateDate,
    planOptions,
    isHasNextPage,
    handleLoadMore,
    resetPagination,
  } = useBaseReportSummaryQuery<ClaimResponse>(
    params.isMultipleEmployer
      ? API_URL.REPORTS_CARD_STATUS_REPORT_MULTI_EMPLOYERS_SUMMARY
      : API_URL.REPORTS_CARD_STATUS_REPORT_SUMMARY,
    [QUERY_KEY, params.employerId, params?.cardStatusGroupType, params.employerIds?.join(',')],
    {
      ...employerId,
      employer_id: params.employerId,
      card_status_report_group_type: params?.cardStatusGroupType ? params?.cardStatusGroupType : undefined,
    },
    {
      isWithTotal: params?.isWithTotal,
      isMultipleEmployers: params.isMultipleEmployer,
      employerId: params.employerId,
      employerIds: params.employerIds,
    }
  );

  useEffect(() => {
    resetPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.employerId, params?.cardStatusGroupType, params.employerIds?.join(',')]);

  const [fromDate, toDate] = useMemo(() => {
    const response = rawData?.data;
    const from = response?.date_from
      ? dayjs(response.date_from, RAW_DATE_FORMAT).format(DEFAULT_DATE_FORMAT)
      : null;
    const to = response?.date_to
      ? dayjs(response.date_to, RAW_DATE_FORMAT).format(DEFAULT_DATE_FORMAT)
      : null;
    return [from, to];
  }, [rawData?.data]);
  const isEmpty = useMemo(() => {
    return !toDate && !fromDate;
  }, [fromDate, toDate]);
  const title = useMemo(() => {
    if (isEmpty) {
      return null;
    }
    if (fromDate || toDate) {
      return `${fromDate || EMPTY_FIELD_VALUE} — ${toDate || EMPTY_FIELD_VALUE}`;
    }
    return EMPTY_FIELD_VALUE;
  }, [fromDate, isEmpty, toDate]);

  return {
    lastTimeUpdateDate,
    planOptions,
    data,
    isLoading,
    isLoadingMore,
    isSuccess,
    isEmptyData,
    title,
    isHasNextPage,
    handleLoadMore,
  };
};

export default useCardStatusReportSummaryQuery;
